import { FC, useCallback, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, Snackbar, Typography } from '@mui/material';

import { ArrowBackIcon } from '../../../../../components/Icons/ArrowBackIcon';
import { CloseIcon } from '../../../../../components/Icons/CloseIcon';
import FlightResultCard from '../../../Discover/components/FlightResultCard/FlightResultCard';
import {
  useCreateTripStopMutation,
  useGetTripStopFlightAlternativesQuery,
} from '../../../../../store/services/tripsApi';
import type { FlightItem } from '../../../../../types/flight.types';
import { getApiErrorMessage } from '../../../../../utils/apiErrorMessage';
import { getFlightItemKey } from '../../../../../utils/flightItemKey';
import { buildFlightTripStopPayload } from '../../../../../utils/flightTripStopPayload';

import styles from './TripFlightAlternativesPanel.module.scss';

interface TripFlightAlternativesPanelProps {
  tripId: number;
  stopId: number;
  numberOfPassengers?: number | null;
  onClose: () => void;
}

const GENERIC_LOAD_ERROR = 'Could not load alternative flights. Please try again later.';

const TripFlightAlternativesPanel: FC<TripFlightAlternativesPanelProps> = ({
  tripId,
  stopId,
  numberOfPassengers,
  onClose,
}) => {
  const travelersCount = useMemo(() => Math.min(9, Math.max(1, numberOfPassengers ?? 1)), [numberOfPassengers]);

  const { data, error, isLoading, isError } = useGetTripStopFlightAlternativesQuery(
    {
      stopId,
      travel_class: 'ECONOMY',
      travelers_count: travelersCount,
    },
    { skip: !Number.isFinite(stopId) }
  );

  const loadErrorMessage = useMemo(() => {
    if (isError && error) {
      return getApiErrorMessage(error, GENERIC_LOAD_ERROR, { firstOnly: true });
    }

    if (!isLoading && data) {
      const items = data.flight_items ?? [];
      if (items.length === 0) {
        const fromPayload = getApiErrorMessage({ data }, '', { firstOnly: true });
        if (fromPayload) {
          return fromPayload;
        }
      }
    }

    return null;
  }, [data, error, isError, isLoading]);

  const [createTripStop] = useCreateTripStopMutation();
  const [savingFlightKey, setSavingFlightKey] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const items = data?.flight_items ?? [];

  const handleSaveFlight = useCallback(
    async (flight: FlightItem, key: string) => {
      setSavingFlightKey(key);

      try {
        await createTripStop({
          tripId: String(tripId),
          activityType: 'Flight',
          activityData: buildFlightTripStopPayload(flight),
        }).unwrap();

        setSnackbar({
          open: true,
          message: 'Flight saved to your trip.',
          severity: 'success',
        });
      } catch (error: unknown) {
        setSnackbar({
          open: true,
          message: getApiErrorMessage(error, 'Could not save flight. Please try again.', { firstOnly: true }),
          severity: 'error',
        });
      } finally {
        setSavingFlightKey(null);
      }
    },
    [createTripStop, tripId]
  );

  return (
    <Box className={styles.tripFlightAlternativesPanel}>
      <Box className={styles.header}>
        <Box className={styles.headerLeft}>
          <IconButton
            onClick={onClose}
            className={styles.backButton}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h7Bold"
            className={styles.title}
          >
            Alternative Flights
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          className={styles.closeButton}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={styles.content}>
        {isLoading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', py: 6 }}
            aria-busy
          >
            <CircularProgress size={40} />
          </Box>
        ) : null}

        {loadErrorMessage ? (
          <Typography
            variant="h10"
            className={styles.stateMessage}
          >
            {loadErrorMessage}
          </Typography>
        ) : null}

        {!isLoading && !loadErrorMessage && items.length === 0 ? (
          <Typography
            variant="h10"
            className={styles.stateMessage}
          >
            No alternative flights found for this stop.
          </Typography>
        ) : null}

        {!isLoading &&
          !loadErrorMessage &&
          items.map((flight, index) => {
            const key = getFlightItemKey(flight, index);
            return (
              <FlightResultCard
                key={key}
                flight={flight}
                isRoundTrip={Boolean(flight.return_departure_date_time && flight.return_arrival_date_time)}
                onSave={f => handleSaveFlight(f, key)}
                showActions
                showShopButton={false}
                isSaving={savingFlightKey === key}
              />
            );
          })}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TripFlightAlternativesPanel;
